import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import MemberSelectTable from 'components/IncidentConfig/MemberSelectTable';
import PositionAssignee from 'components/IncidentConfig/PositionAssignee';
import AttachResourceToSeatDialog from 'components/IncidentConfig/roster/AttachResourceToSeatDialog';
import SeatResourceRow from 'components/IncidentConfig/roster/SeatResourceRow';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useOptionalCreateRosterDraft } from 'components/IncidentConfig/CreateIncident/CreateRosterDraftContext';
import {
  useAddUsersToIncidentRoster,
  useGetRostersForGroup,
  useGetSeatResourcesForRoster,
} from 'hooks/incident/useRoster';
import React, { useMemo, useState } from 'react';
import AssignNewExternalUserForm from './AssignNewExternalUserForm';

/**
 * Only one assign panel is open at a time. Keeping them in a single value makes
 * that mutually exclusive by construction, rather than by remembering to clear
 * the others in every handler.
 */
type AssignPanel = 'leader' | 'remoteLeader' | 'users' | 'remoteUsers' | null;

/**
 * This column renders inside a shadcn FormModal (overlay z-50). The menu
 * portals to the body, so it needs a z-index above the dialog overlay and still
 * below leftover Bootstrap tooltip layers.
 */
const MENU_ABOVE_DIALOG = 'tw:z-[60]';

type PositionAssigneesColumnProps = {
  incidentId: string;
  groupId: string;
  positionId: string;
  positionName: string;
  isWorkgroup: boolean;
  members: RosterEntry[];
  rostersForGroupQuery: ReturnType<typeof useGetRostersForGroup>;
  isDraft: boolean;
  title: string;
  emptyState?: React.ReactNode;
  /**
   * Draft-roster members for this position, threaded from the Active column
   * only.
   */
  draftMembers?: RosterEntry[];
};

const PositionAssigneesColumn: React.FC<PositionAssigneesColumnProps> = ({
  incidentId,
  groupId,
  positionId,
  positionName,
  isWorkgroup,
  members,
  rostersForGroupQuery,
  isDraft,
  title,
  emptyState,
  draftMembers,
}) => {
  const [activePanel, setActivePanel] = useState<AssignPanel>(null);
  const [isAttachingResource, setIsAttachingResource] = useState(false);
  const isCreateMode = !!useOptionalCreateRosterDraft();

  const addUsersToIncident = useAddUsersToIncidentRoster(incidentId, groupId);

  const { data: seatResources = [] } = useGetSeatResourcesForRoster(isDraft);

  // True when a draft roster exists; used to show/hide the → Draft button in PositionAssignee
  const showScheduleForNextOp = draftMembers !== undefined;

  // Picking the option that is already open closes it, matching how the buttons these menus replaced behaved.
  const togglePanel = (panel: Exclude<AssignPanel, null>) =>
    setActivePanel((current) => (current === panel ? null : panel));

  const membersWithLeader = useMemo(() => {
    return members.map((member) => ({
      ...member,
      isLeader:
        member.incidentPositions?.find((pos) => pos.positionId === positionId)
          ?.isLeader || false,
    }));
  }, [members, positionId]);

  const positionLeader = useMemo(
    () => membersWithLeader.find((member) => member.isLeader),
    [membersWithLeader]
  );

  const positionAssignees = useMemo(
    () => membersWithLeader.filter((member) => !member.isLeader),
    [membersWithLeader]
  );

  const seatResourcesForPosition = useMemo(
    () =>
      seatResources.filter(
        (seatResource) => seatResource.positionId === positionId
      ),
    [seatResources, positionId]
  );

  const isAssigning = activePanel !== null;

  const isAlreadyOnDraft = (userId: string) =>
    draftMembers?.some((m) => m.userId === userId) ?? false;

  if (
    emptyState !== undefined &&
    members.length === 0 &&
    seatResourcesForPosition.length === 0 &&
    !isAssigning
  ) {
    return (
      <div>
        <h4>{title}</h4>
        {emptyState}
      </div>
    );
  }

  return (
    <div>
      <h4>{title}</h4>
      {isWorkgroup && (
        <div>
          <div className="d-flex justify-content-between align-items-center width-100 flex-row">
            <h5 className="m-0">Leader</h5>
            <div className="d-flex justify-content-between align-items-center flex-row">
              {positionLeader && (
                <SharedIcon
                  iconName="info"
                  size="sm"
                  color="primary"
                  title="You cannot assign more than one leader. Please delete the existing leader before assigning a new one."
                  aria-label="You cannot assign more than one leader. Please delete the existing leader before assigning a new one."
                  style={{ cursor: 'default' }}
                />
              )}
              <div className="dark ms-2">
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!!positionLeader}
                      >
                        + Assign Leader
                      </Button>
                    }
                  />
                  {/* The popup portals to the body, so it carries `dark` itself rather than inheriting it. */}
                  <DropdownMenuContent
                    className="dark"
                    positionerClassName={MENU_ABOVE_DIALOG}
                    align="end"
                    sideOffset={4}
                  >
                    <DropdownMenuItem onClick={() => togglePanel('leader')}>
                      Assign Leader
                    </DropdownMenuItem>
                    {!isCreateMode ? (
                      <DropdownMenuItem
                        onClick={() => togglePanel('remoteLeader')}
                      >
                        Assign New External Leader
                      </DropdownMenuItem>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
          <hr className="mt-2 mb-4" />
        </div>
      )}
      {activePanel === 'leader' && (
        <MemberSelectTable
          groupRoster={rostersForGroupQuery}
          currentPositionMembers={membersWithLeader}
          onSelectMembers={(selectedMembers) =>
            addUsersToIncident.mutate({
              positionAssignments: selectedMembers.map((member) => ({
                positionId,
                positionName,
                incidentMemberUserId: member.user_guid,
                isLeader: true,
              })),
              isDraft,
            })
          }
          closeTable={() => setActivePanel(null)}
          selectingLeader={true}
        />
      )}
      {activePanel === 'remoteLeader' && (
        <AssignNewExternalUserForm
          incidentId={incidentId}
          positionId={positionId}
          positionName={positionName}
          selectingLeader={true}
          closeTable={() => setActivePanel(null)}
        />
      )}
      {positionLeader ? (
        <div className="mb-4">
          <PositionAssignee
            incidentId={incidentId}
            groupId={groupId}
            positionId={positionId}
            positionName={positionName}
            positionMember={positionLeader}
            isDraft={isDraft}
            draftExists={showScheduleForNextOp}
            alreadyOnDraftForPosition={isAlreadyOnDraft(positionLeader.userId)}
          />
        </div>
      ) : null}
      <div>
        <div className="d-flex justify-content-between align-items-center width-100 flex-row">
          <h5 className="m-0">Assignees</h5>
          <div className="dark d-flex justify-content-between align-items-center flex-row">
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button variant="outline" size="sm">
                    + Assign
                  </Button>
                }
              />
              <DropdownMenuContent
                className="dark"
                positionerClassName={MENU_ABOVE_DIALOG}
                align="end"
                sideOffset={4}
              >
                <DropdownMenuItem onClick={() => togglePanel('users')}>
                  Assign User
                </DropdownMenuItem>
                {!isCreateMode ? (
                  <>
                    <DropdownMenuItem
                      onClick={() => togglePanel('remoteUsers')}
                    >
                      Assign New External User
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setIsAttachingResource(true)}
                    >
                      Assign Resource
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <hr className="mt-2 mb-4" />
      </div>
      {activePanel === 'remoteUsers' && (
        <AssignNewExternalUserForm
          incidentId={incidentId}
          positionId={positionId}
          positionName={positionName}
          selectingLeader={false}
          closeTable={() => setActivePanel(null)}
        />
      )}
      {activePanel === 'users' && (
        <MemberSelectTable
          groupRoster={rostersForGroupQuery}
          currentPositionMembers={membersWithLeader}
          selectingLeader={false}
          onSelectMembers={(selectedMembers) =>
            addUsersToIncident.mutate({
              positionAssignments: selectedMembers.map((member) => ({
                positionId,
                positionName,
                incidentMemberUserId: member.user_guid,
                isLeader: false,
              })),
              isDraft,
            })
          }
          closeTable={() => setActivePanel(null)}
        />
      )}
      {positionAssignees.map((assignee, index) => (
        <div key={assignee.incidentMemberId}>
          <PositionAssignee
            incidentId={incidentId}
            groupId={groupId}
            positionId={positionId}
            positionName={positionName}
            positionMember={assignee}
            isDraft={isDraft}
            draftExists={showScheduleForNextOp}
            alreadyOnDraftForPosition={isAlreadyOnDraft(assignee.userId)}
          />
          {index < positionAssignees.length - 1 && (
            <hr className="mt-4 mb-4 opacity-50" />
          )}
        </div>
      ))}
      {seatResourcesForPosition.length > 0 && (
        <div className="mt-4">
          <h5 className="m-0">Resources</h5>
          <hr className="mt-2 mb-4" />
          {seatResourcesForPosition.map((seatResource, index) => (
            <div key={seatResource.id}>
              <SeatResourceRow seatResource={seatResource} />
              {index < seatResourcesForPosition.length - 1 && (
                <hr className="mt-4 mb-4 opacity-50" />
              )}
            </div>
          ))}
        </div>
      )}

      {isAttachingResource && (
        <AttachResourceToSeatDialog
          show
          onClose={() => setIsAttachingResource(false)}
          positionId={positionId}
          positionName={positionName}
          isDraft={isDraft}
        />
      )}
    </div>
  );
};

export default PositionAssigneesColumn;
